/* eslint-disable @typescript-eslint/no-explicit-any */
import * as d3 from 'd3';
import { isDesktop } from 'react-device-detect';

import { stockingPhaseTypes } from '../../config/commons';
import { pathIndeterminateCircleIcon } from '../../common/components/Icon/IndeterminateCircleIcon';

import { Animal, AnimalsByGroup, Percentile } from './interfaces';

export interface SampleD3Props {
  container: HTMLDivElement | null;
  width: number;
  height: number;
  urlImage: string;
  animalsByGroup: Percentile[] | AnimalsByGroup[];
  phaseType: string;
  showAnimalsRemoved?: boolean;
  translateX?: number;
  translateY?: number;
  enableScroll?: boolean;
  sightIconColor?: string;
  currentIndex?: number;
}

export interface EditSampleD3Props {
  animalsByGroup: Percentile[] | AnimalsByGroup[];
  showAnimalsRemoved?: boolean;
  currentIndex?: number;
  urlImage: string;
}

interface PointProps {
  fill: string;
  r: number;
  x: number;
  y: number;
  stroke: string;
  strokeWidth: number;
  dash: number[];
  animal: Animal;
  group: Percentile | AnimalsByGroup;
  hasDisease?: boolean;
}

export default class SampleD3 {
  container: HTMLDivElement | null;
  svg: d3.Selection<SVGSVGElement, PointProps, null, undefined> = d3.select<SVGSVGElement, PointProps>(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
  svgGroup: d3.Selection<SVGGElement, PointProps, null, undefined> = d3.select<SVGGElement, PointProps>(document.createElementNS('http://www.w3.org/2000/svg', 'g'));
  svgImage: d3.Selection<SVGImageElement, PointProps, null, undefined> = d3.select<SVGImageElement, PointProps>(document.createElementNS('http://www.w3.org/2000/svg', 'image'));
  svgCircle: d3.Selection<SVGCircleElement, PointProps, null, undefined> = d3.select<SVGCircleElement, PointProps>(document.createElementNS('http://www.w3.org/2000/svg', 'circle'));

  svgCloseIcon: d3.Selection<SVGImageElement, PointProps, null, undefined> = d3.select<SVGImageElement, PointProps>(document.createElementNS('http://www.w3.org/2000/svg', 'image'));
  svgVirusIcon: d3.Selection<SVGImageElement, PointProps, null, undefined> = d3.select<SVGImageElement, PointProps>(document.createElementNS('http://www.w3.org/2000/svg', 'image'));

  width = 0;
  height = 0;
  urlImage = '';
  animalsByGroup;
  phaseType = '';
  showAnimalsRemoved;
  translateX;
  translateY;
  enableScroll = true;
  sightIconColor;
  currentIndex = 0;
  previousIndex = 0;

  dragging = false;
  startCoords = [0, 0];
  offset = {
    x: 0,
    y: 0,
  };
  lastTouchEnd = 0;

  // eslint-disable-next-line
  constructor(props: SampleD3Props) {
    const {
      container,
      width,
      height,
      urlImage,
      animalsByGroup,
      phaseType,
      showAnimalsRemoved = true,
      translateX = 0,
      translateY = 0,
      enableScroll = true,
      sightIconColor,
    } = props;

    this.container = container;
    this.width = width;
    this.height = height;
    this.urlImage = urlImage;
    this.animalsByGroup = animalsByGroup;
    this.phaseType = phaseType;
    this.showAnimalsRemoved = showAnimalsRemoved;
    this.translateX = translateX;
    this.translateY = translateY;
    this.enableScroll = enableScroll;
    this.sightIconColor = sightIconColor;

    this.createSvgElement();
    this.renderImage();

    if (enableScroll) {
      this.applyScrollToSvg();
    }

    this.renderCircles();
  }

  refreshChart (props: EditSampleD3Props) {
    const { animalsByGroup, showAnimalsRemoved = true, currentIndex = 0, urlImage } = props;

    this.animalsByGroup = animalsByGroup;
    this.showAnimalsRemoved = showAnimalsRemoved;
    this.urlImage = urlImage;

    if (this.currentIndex !== currentIndex) {
      this.previousIndex = this.currentIndex;
    }

    this.currentIndex = currentIndex;

    if (this.previousIndex !== this.currentIndex) {
      this.renderImage();
    }

    if (this.enableScroll) {
      this.applyScrollToSvg();
    }

    this.renderCircles();

    this.updateBackgroundColor('black');
  }

  createSvgElement () {
    const { container, height, width, translateX, translateY } = this;

    d3.select(container).select('svg').remove();

    this.svg = d3.select<HTMLDivElement | null, PointProps>(container)
      .append('svg')
      .attr('id', 'svg')
      .attr('width', width)
      .attr('height', height);

    this.svgGroup = this.svg
      .append('g')
      .attr('id', 'svg-group')
      .attr('transform', `translate(${translateX}, ${translateY})`);
  }

  renderImage () {
    const { container, urlImage } = this;

    d3.select(container).select('#svgImage').remove();

    this.svgImage = this.svgGroup.append('image')
      .attr('id', 'svgImage')
      .attr('xlink:href', urlImage)
      .attr('width', '100%')
      .attr('height', '100%');
  }

  getCoords (event: TouchEvent) {
    if (isDesktop) {
      return d3.pointer(event);
    }

    if (!event.touches) {
      return [this.startCoords[0], this.startCoords[1]];
    }

    const touch = event.touches[0];
    return [touch.clientX, touch.clientY];
  }

  mouseDown (event: TouchEvent) {
    this.dragging = true;
    this.startCoords = this.getCoords(event);
    this.svgGroup.style('cursor', 'grabbing');
  }

  mouseUp () {
    if (!this.dragging) return;

    this.dragging = false;
    this.svgGroup.style('cursor', 'grab');
  }

  mouseMove (event: TouchEvent) {
    if (!this.dragging) return;

    const { height, width } = this;
    const currentCoords = this.getCoords(event);

    const extraHeight = 70;
    const deltaX = (currentCoords[0] - this.startCoords[0]) * 0.8;
    const deltaY = (currentCoords[1] - this.startCoords[1]) * 0.8;

    this.offset.x += deltaX;
    this.offset.y += deltaY;

    this.offset.x = Math.min(0, Math.max(window.innerWidth - width, this.offset.x));
    this.offset.y = Math.min(0, Math.max(window.innerHeight - height - extraHeight, this.offset.y));

    this.svgGroup.attr('transform', `translate(${this.offset.x}, ${this.offset.y})`);
    this.startCoords = currentCoords;
  }

  touchStart (event: TouchEvent) {
    this.dragging = true;
    this.startCoords = this.getCoords(event);
    this.svgGroup.style('cursor', 'grabbing');

    const currentTime = new Date().getTime();
    this.lastTouchEnd = currentTime;
  }

  applyScrollToSvg () {
    this.svgGroup.on('mousedown', (event: TouchEvent) => this.mouseDown(event));
    this.svgGroup.on('mouseup', () => this.mouseUp());
    this.svgGroup.on('mousemove', (event: TouchEvent) => this.mouseMove(event));

    this.svgGroup.on('touchstart', (event: TouchEvent) => this.touchStart(event));
    this.svgGroup.on('touchend', () => this.mouseUp());
    this.svgGroup.on('touchmove', (event: TouchEvent) => this.mouseMove(event));
  }

  isEditedAnimal (animal: Animal) {
    return animal.previousGroup !== undefined || animal.added || animal.disease !== undefined;
  }

  isAdultPhaseType () {
    const { phaseType } = this;
    return phaseType === stockingPhaseTypes.ADULT;
  }

  getStrokeColor (animal: Animal) {
    if (this.isEditedAnimal(animal) || this.isAdultPhaseType()) {
      return 'white';
    }

    return '';
  }

  getStrokeWidth (animal: Animal) {
    if (this.isEditedAnimal(animal) || this.isAdultPhaseType()) {
      return 1.5;
    }

    return 0;
  }

  getDash (animal: Animal) {
    if (this.isEditedAnimal(animal)) {
      return [3, 2];
    }

    return [];
  }

  getAnimalData (props: { group: Percentile | AnimalsByGroup; animal: Animal }) {
    const { animal, group } = props;

    const hasDisease = !!animal.disease;
    const data: PointProps = {
      fill: group.color,
      r: group.radius,
      x: animal.x,
      y: animal.y,
      stroke: this.getStrokeColor(animal),
      strokeWidth: this.getStrokeWidth(animal),
      dash: this.getDash(animal),
      animal,
      group,
      hasDisease,
    };
    return data;
  }

  getAnimalsRemoved (props: { group: Percentile | AnimalsByGroup; animal: Animal }) {
    const { animal, group } = props;

    const data: PointProps = {
      fill: group.color,
      r: group.radius,
      x: animal.x,
      y: animal.y,
      stroke: '',
      strokeWidth: 0,
      dash: [],
      animal,
      group,
    };

    return data;
  }

  renderSievedAnimals (props: { data: PointProps[]; }) {
    const { data } = props;

    // eslint-disable-next-line
    // @ts-ignore
    this.svgCloseIcon = this.svgGroup
      .append('g')
      .attr('id', 'sievedAnimalsGroup')
      .selectAll('path')
      .data(data)
      .enter()
      .append('path')
      .attr('d', pathIndeterminateCircleIcon)
      .style('fill', 'black')
      .attr('transform', (point: PointProps) => {
        // this icon is designed centered at (0,0), size 24x24
        // we use the radius as scale to adjust size
        const scale = point.r / 12; // because 12 is the base radius of the path
        return `translate(${point.x - (point.r * 1)}, ${point.y - (point.r * 1)}) scale(${scale})`;
      });
  }

  renderAnimals (props: { data: PointProps[] }) {
    const { data } = props;

    // eslint-disable-next-line
    // @ts-ignore
    this.svgCircle = this.svgGroup
      .append('g')
      .attr('id', 'animals')
      .selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('r', (point: PointProps) => point.r)
      .attr('cx', (point: PointProps) => point.x)
      .attr('cy', (point: PointProps) => point.y)
      .attr('fill', (point: PointProps) => point.fill)
      .attr('stroke', (point: PointProps) => point.stroke)
      .attr('stroke-dasharray', (point: PointProps) => point.dash)
      .attr('stroke-width', (point: PointProps) => point.strokeWidth);
  }

  /* eslint-disable max-depth*/
  renderCircles () {
    const { animalsByGroup, container, showAnimalsRemoved } = this;

    const circleData: PointProps[] = [];
    const sickAnimals: PointProps[] = [];
    const removeAnimals: PointProps[] = [];
    const sievedAnimals: PointProps[] = [];

    d3.select(container).select('#removeAnimalGroup').remove();
    d3.select(container).select('#animals').remove();
    d3.select(container).select('#sickAnimals').remove();
    d3.select(container).select('#circleSelected').remove();
    d3.select(container).select('#sievedAnimalsGroup').remove();
    d3.select(container).select('#removeAnimalGroup').remove();

    for (let i = 0; i < animalsByGroup.length; i++) {
      const group = animalsByGroup[i];

      for (let index = 0; index < group.animals.length; index++) {
        const animal = group.animals[index];

        if (!group.isActive && !animal.removed) {
          continue;
        }

        if (group.isActive && group.sieved) {
          const data: PointProps = this.getAnimalsRemoved({ animal, group });
          sievedAnimals.push(data);
          continue;
        }

        if (animal.disease) {
          const data: PointProps = this.getAnimalsRemoved({ animal, group });
          sickAnimals.push(data);
        }

        if (animal.disease) {
          const data: PointProps = this.getAnimalsRemoved({ animal, group });
          sickAnimals.push(data);
        }

        if (animal.removed) {
          if (showAnimalsRemoved) {
            const data: PointProps = this.getAnimalsRemoved({ animal, group });
            removeAnimals.push(data);
          }
          continue;
        }

        const data: PointProps = this.getAnimalData({ animal, group });
        circleData.push(data);
      }
    }

    this.renderSievedAnimals({ data: sievedAnimals });
    this.renderAnimals({ data: circleData });
  }

  updateBackgroundColor (color: string) {
    this.sightIconColor = color;

    d3.select('#background-color-filter feFlood')
      .attr('flood-color', color);
  }
}
